import { Component, ElementRef, EventEmitter, HostListener, Input, Output, QueryList, ViewChildren } from '@angular/core';
import { Router } from '@angular/router';
import { DeviceService } from 'src/app/core/device.service';
import { FlashcardService } from 'src/app/core/flashcard.service';
import { PdfService } from 'src/app/core/pdf.service';
import { Learnset } from 'src/app/models/learnset.model';

@Component({
  selector: 'app-flashcards-check-step',
  templateUrl: './flashcards-check-step.component.html',
  styleUrls: ['./flashcards-check-step.component.scss'],
  standalone: false
})
export class FlashcardsCheckStep {

  @Input() files?: any[];
  @Input() learnset?: Learnset | null;
  @Input() isDemo: boolean = false;
  @Input() pdfSrc?: any;
  @Input() pages?: any[];
  @Output() pagesChange: EventEmitter<any[]> = new EventEmitter<any[]>();
  @Output() navigateBack: EventEmitter<void> = new EventEmitter<void>();

  filteredPages: any[] = [];

  currentPage: number = 1;
  pdfPagesCount?: number;

  @ViewChildren('filePageContainer') filePageContainers!: QueryList<ElementRef>;
  observer!: IntersectionObserver;

  deviceSize: 'mobile' | 'tablet' | 'desktop';
  
  isLoading: boolean = false;

  quantityOptions: { title: string; text: string, value: string }[] = [
    { title: '0 Karten', text: 'Essentielle Karten', value: 'priority-1' },
    { title: '0 Karten', text: 'Wichtige Karten', value: 'priority-2' },
    { title: 'Alle', text: 'Alle Karten', value: 'priority-3' }
  ];
  selQuantityOption: { title: string, text: string, value: string } = this.quantityOptions[0];

  prio1Count: number = 0;
  prio2Count: number = 0;
  prio3Count: number = 0;
  totalCount: number = 0;

  constructor(
    private deviceService: DeviceService,
    private pdfService: PdfService,
    private flashcardService: FlashcardService,
    private router: Router
  ) {
    this.deviceSize = this.deviceService.getDeviceSize();
  }

  ngAfterViewInit(): void {
    if (this.deviceSize === 'desktop') {
      this.initIntersectionObserver();

      this.filePageContainers.forEach((container) =>
        this.observer.observe(container.nativeElement)
      );
    }
  }

  ngOnInit() {
    // wenn pdfSrc definiert ist, dann ist es nur eine Datei und diese Datei ist eine PDF
    if (this.pdfSrc) {
      this.pdfService.getPdfPageCount(this.files![0]).then(num => this.pdfPagesCount = num);
    }
    this.updateQuantities();
    this.filterFlashcards();
  }

  filterFlashcards() {
    this.filteredPages = this.pages!;
    if (this.selQuantityOption.value === 'priority-1') {
      this.filteredPages = this.filteredPages.map(page => 
        page?.filter((flashcard: any) => flashcard.priority === 1)
      );
    } else if (this.selQuantityOption.value === 'priority-2') {
      this.filteredPages = this.filteredPages.map(page => 
        page?.filter((flashcard: any) => flashcard.priority === 1 || flashcard.priority === 2)
      );
    } else {
      this.filteredPages = this.filteredPages.map(page => 
        page?.filter((flashcard: any) => flashcard.priority === 1 || flashcard.priority === 2 || flashcard.priority === 3)
      );
    }
    this.totalCount = this.filteredPages.flat()?.filter((flashcard) => flashcard).length;
  }

  updateQuantities() {
    const allCards: any = this.pages?.flat();

    this.prio1Count = allCards.filter((card: any) => card && (card.priority === 1)).length;
    this.prio2Count = allCards.filter((card: any) => card && (card.priority === 1 || card.priority === 2)).length;
    this.prio3Count = allCards.filter((card: any) => card && (card.priority === 1 || card.priority === 2 || card.priority === 3)).length;

    this.quantityOptions[0].title = this.prio1Count + ' Karten';
    this.quantityOptions[1].title = this.prio2Count + ' Karten';
    this.quantityOptions[2].title = this.prio3Count + ' Karten';
  }

  previousPage() {
    this.currentPage = this.currentPage > 1 ? this.currentPage - 1 : 1;
    // this.scrollToPage(this.currentPage);
  }

  nextPage() {
    this.currentPage = this.currentPage < this.pdfPagesCount! ? this.currentPage + 1 : this.pdfPagesCount!;
    // this.scrollToPage(this.currentPage);
  }

  scrollToPage(pageNumber: number): void {
    const container = this.filePageContainers.toArray()[pageNumber - 1];
    container?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  onPdfViewerPageChange(pageNumber: number) {
    this.currentPage = pageNumber;
    this.scrollToPage(pageNumber);
  }

  initIntersectionObserver(): void {
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = this.filePageContainers
              .toArray()
              .findIndex((el) => el.nativeElement === entry.target);

            if (index !== -1 && this.filteredPages![index] !== null) {
              this.currentPage = index || this.currentPage;
            }
          }
        });
      },
      {
        root: null, // Standard: gesamtes Viewport
        threshold: 0.5, // 50% des Elements müssen sichtbar sein
      }
    );
  }

  handleFlashcardAction(page: any, flashcard: any, action: string) {
    if (action === 'delete') {
      const matches = (f: any) =>
        f.question === flashcard.question &&
        f.answer === flashcard.answer &&
        f.page === flashcard.page &&
        f.priority === flashcard.priority;

      // Original pages
      this.pages = this.pages!.map(p => p.filter((f: any) => !matches(f)));

      // Filtered pages
      this.filteredPages = this.filteredPages!.map(p => p.filter((f: any) => !matches(f)));

      
      this.updateQuantities();
      this.totalCount = this.filteredPages.flat()?.filter((flashcard) => flashcard).length;

      this.pagesChange.emit(this.pages!);
    }
  }

  goBack() {
    this.navigateBack.emit();
  }

  saveFlashcards() {
    if (this.isDemo) {
      return;
    }

    this.isLoading = true;

    let flashcards: any = this.filteredPages?.flat();
    flashcards = flashcards.filter((flashcard: any) => flashcard != null);
    flashcards.map((flashcard: any) => {
      flashcard.learnset_id = this.learnset!.id;
      return flashcard;
    });

    this.flashcardService.saveFlashcards(flashcards, 'file-upload').subscribe((result: any) => {
      this.router.navigate([`/sets/${this.learnset!.id}`], { queryParams: { newCardsAdded: true }});
      this.isLoading = false;
    }, () => {
      this.isLoading = false;
    });
  }

  ngOnDestroy(): void {
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  @HostListener('window:beforeunload', ['$event'])
  handleBeforeUnload(event: Event): void {
    event.preventDefault();
    (event as BeforeUnloadEvent).returnValue = '';
  }
}
